import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ChevronDown, Check } from 'lucide-react';
import ColorPainter from '../components/ColorPainter';

const heroImages = [
  'https://www.jotun.com/contentassetsjot03/aac928d001f8418f9491ca3efc8eb71b/drygolin-produkter-2.jpg?width=800&height=600&quality=85',
  'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/dc475fd11_7b46b8b8-8353-48bf-aaaa-2130501a29c8.jpg',
  'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/3b7edf984_a0efa3e3-844a-4529-92f7-54e2f71ccd01.jpg'
];

export default function ExteriorPainting() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [heroIndex, setHeroIndex] = useState(0);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <div className="pt-20 pb-20">
        {/* Hero */}
        <div className="relative h-[500px] md:h-[600px] mb-32 overflow-hidden rounded-[12px] mx-6 md:mx-12">
          <img
            src={heroImages[heroIndex]}
            alt="Eksteriormaling produkter"
            className="w-full h-full object-cover transition-opacity duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex flex-col justify-end p-12">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="text-sm tracking-widest-2xl font-body uppercase text-white mb-6">Personalisering</p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light leading-[1.1] text-white mb-6">
                Badstuen i<br /><span className="italic">Din Farge</span>
              </h1>
              <p className="font-body text-white/80 text-2xl max-w-2xl leading-relaxed">
                Velg en farge som speiler din personlighet og harmonerer med omgivelsene. Les om beskyttelse, vedlikehold og langsiktig holdbarhet av malt kledning.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-12">

          {/* Hvorfor Male */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="mb-32">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Beslutning</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">Hvorfor <span className="italic">Male</span></h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { title: 'Strukturell Beskyttelse', desc: 'Malingslaget beskytter treverket mot UV, fuktighet og frost. Ekte investering i holdbarhet over 15–20 år.' },
                { title: 'Personlig Estetikk', desc: 'En tydelig farge som passer perfekt til husets arkitektur og det naturlige landskapet rundt.' },
                { title: 'Holdbart Malingslag', desc: 'Profesjonell maling med riktig vedlikehold holder i tiår. Enkel fornyelse hvert 15–20 år.' }
              ].map((item, idx) => (
                <motion.div key={idx} whileHover={{ y: -4 }} className="bg-white rounded-[8px] p-8 border border-border/20 hover:border-[#2d5a3d] transition-colors">
                  <h3 className="font-heading text-2xl font-light mb-4 text-[#2a1810]">{item.title}</h3>
                  <p className="font-body text-2xl text-foreground/70">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Valg av Maling */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mb-16">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Malingvalg</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-8 text-[#2a1810]">Hvilken Maling <span className="italic">Velger Vi?</span></h2>
            <p className="font-body text-foreground/70 text-2xl leading-relaxed mb-16 max-w-3xl">
              På markedet finnes det mange typer eksteriormaling — fra rimelige standardprodukter til avanserte hybridmalinger. Vi går alltid for det beste for å sikre at din badstue holder seg vakker i tiår.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="bg-white rounded-[8px] border border-border/20 p-8">
                <h3 className="font-heading text-xl font-light mb-4 text-[#2a1810]">Typer maling på markedet</h3>
                <div className="space-y-4">
                  {[
                    { type: 'Tradisjonell oljedekkbeis', desc: 'Klassisk løsning, god dekkevne, men må males hvert 4–5 år og er tyngre å påføre.' },
                    { type: 'Akrylmaling', desc: 'Vannbasert, rask tørketid, lavt vedlikehold. Fungerer godt i de fleste klimaforhold.' },
                    { type: 'Hybridmaling (olje + akryl)', desc: 'Beste alternativet: kombinerer oljas dybde og akrylens holdbarhet. Vedlikeholdsintervall opptil 16 år.' },
                    { type: 'Lazur/beis', desc: 'Halvtransparent, fremhever treverks naturlige utseende. Krever hyppigere fornyelse.' }
                  ].map((item, idx) => (
                    <div key={idx} className="border-b border-border/20 pb-4 last:border-0 last:pb-0">
                      <p className="font-heading text-2xl font-light text-[#2a1810] mb-1">{item.type}</p>
                      <p className="font-body text-2xl text-foreground/60">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#2d5a3d]/5 border-2 border-[#2d5a3d]/30 rounded-[8px] p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 rounded-full bg-[#2d5a3d]"></div>
                  <h3 className="font-heading text-xl font-light text-[#2a1810]">Hva vi velger — og hvorfor</h3>
                </div>
                <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-6">
                  Vi bruker utelukkende <strong className="text-2xl font-bold text-foreground/90">DRYGOLIN Nordic Extreme Supermatt</strong> fra Jotun — en premium hybridmaling designet spesifikt for nordisk klima. Den kombinerer oljebasert grunnlak med akrylbasert topplakk for maksimal holdbarhet, motstand mot slitasje og elegant matt finish.
                </p>
                <div className="space-y-3">
                  {[
                    'Vedlikeholdsintervall opptil 16 år',
                    'Beskyttelse mot svertesopp og alger',
                    'Skittavvisende overflate — krever mindre renhold',
                    'To strøk kan påføres på én dag',
                    'Stabile farger — mørke nyanser falmer ikke'
                  ].map((point, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <Check size={16} className="text-[#2d5a3d] flex-shrink-0 mt-0.5" />
                      <p className="font-body text-2xl text-foreground/70">{point}</p>
                    </div>
                  ))}
                </div>
                <motion.a whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} href="/produkter-maling"
                  className="mt-6 inline-block px-4 py-2 bg-[#2d5a3d]/20 border border-[#2d5a3d] text-[#2d5a3d] font-body text-base tracking-widest rounded-[4px] hover:bg-[#2d5a3d]/30 transition-all">
                  LES MER OM PRODUKTET
                </motion.a>
              </div>
            </div>
          </motion.section>

          {/* Farger */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mb-32">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Farger</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">Tilgjengelige Farger & <span className="italic">Deres Karakter</span></h2>
            <div className="space-y-12">
              <div>
               <h3 className="font-heading text-2xl font-light mb-8 text-[#2a1810]">Malt kledning - Klassisk og moderne</h3>
               <div className="grid md:grid-cols-4 gap-6">
                 {[
                   { name: 'Malt kledning RAL', desc: 'Velg din ønskede RAL-farge', img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/015698a3c_Zrzutekranu2026-04-10o093555.png' },
                   { name: 'Mørk brunn', img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/e93570641_Mrkbrunn.jpg' },
                   { name: 'Malt kledning - Sort', img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/f02797b5c_Sortkledning.jpg' },
                   { name: 'Lys grå', img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/9438e5e7d_Lysgrakledning.jpg' }
                 ].map((color, idx) => (
                   <motion.div key={idx} whileHover={{ scale: 1.05 }} className="text-center">
                     <div className="rounded-[8px] h-32 mb-4 border-2 border-border/30 overflow-hidden">
                       <img src={color.img} alt={color.name} className="w-full h-full object-cover" />
                     </div>
                     <p className="font-heading text-2xl font-light text-[#2a1810]">{color.name}</p>
                     {color.desc && <p className="font-body text-2xl text-foreground/50 mt-1">{color.desc}</p>}
                   </motion.div>
                 ))}
                </div>
              </div>
              <div>
               <h3 className="font-heading text-2xl font-light mb-8 text-[#2a1810]">Naturlige Nyanser</h3>
               <div className="grid md:grid-cols-4 gap-6">
                 {[
                   { name: 'Termofuru Olje', img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/91720c950_Termofuru-glattkant-3.jpg' },
                   { name: 'Kebony Radiata', img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/76749bdf0_2KebonyClearRadiata.png' },
                   { name: 'Kebony Patina Grå', img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/0733a631f_NaturligpatinagraKebony.png' },
                   { name: 'Mørk blå', img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/716ba4a41_KledningMrkbla.jpg' }
                 ].map((color, idx) => (
                   <motion.div key={idx} whileHover={{ scale: 1.05 }} className="text-center">
                     <div className="rounded-[8px] h-32 mb-4 border-2 border-border/30 overflow-hidden">
                       <img src={color.img} alt={color.name} className="w-full h-full object-cover" />
                     </div>
                     <p className="font-heading text-2xl font-light text-[#2a1810]">{color.name}</p>
                     </motion.div>
                     ))}
                     </div>
                     </div>
            </div>
          </motion.section>

          {/* Color Painter */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }} className="mb-32">
            <ColorPainter />
          </motion.section>


          {/* Sammenligning */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className="mb-32">
            <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-6">Sammenligning</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">Kebony vs Maling: <span className="italic">Hva velger du?</span></h2>
            <div className="overflow-x-auto border border-border/40 rounded-[8px]">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#f5f5f5] border-b border-border/40">
                    <th className="px-6 py-4 text-left font-heading text-2xl font-light text-[#2a1810]">Aspekt</th>
                    <th className="px-6 py-4 text-left font-heading text-2xl font-light text-[#2a1810]">Kebony</th>
                    <th className="px-6 py-4 text-left font-heading text-2xl font-light text-[#2a1810]">Maling</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { aspect: 'Vedlikehold', kebony: 'Minimalt (årlig inspeksjon)', paint: 'Regelmessig (rengjøring hvert 2–3 år, full maling hvert 15–20 år)' },
                    { aspect: 'Utseende', kebony: 'Naturlig sølvgrå patina (vakker aldring)', paint: 'Fast farge (kan fornyes)' },
                    { aspect: 'Holdbarhet', kebony: '30+ år, potensielt generasjoner', paint: '15–20 år per malingslag, deretter fornyelse' },
                    { aspect: 'Fargevalg', kebony: 'Begrenset (naturlig tre)', paint: 'Full frihet (uendelige fargealternativer)' },
                    { aspect: 'Filosofi', kebony: 'La naturen arbeide (slow living)', paint: 'Kontrollert estetikk (presist planlagt)' }
                  ].map((row, idx) => (
                    <tr key={idx} className="border-b border-border/20 hover:bg-[#f9f9f9] transition-colors">
                      <td className="px-6 py-4 font-heading text-2xl font-light text-[#2a1810]">{row.aspect}</td>
                      <td className="px-6 py-4 font-body text-2xl text-foreground/80 leading-relaxed">{row.kebony}</td>
                      <td className="px-6 py-4 font-body text-2xl text-foreground/80 leading-relaxed">{row.paint}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.section>

          {/* Vedlikehold Link */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.55 }} className="mb-32">
            <div className="bg-gradient-to-br from-[#2d5a3d]/10 to-[#4a8f63]/5 border-2 border-[#2d5a3d]/20 rounded-[8px] p-12 text-center">
              <p className="text-base tracking-widest-2xl font-body uppercase text-primary mb-4">Vedlikehold</p>
              <h2 className="font-heading text-3xl font-light leading-[1.1] mb-6 text-[#2a1810]">Vedlikeholdsplan <span className="italic">Over Tid</span></h2>
              <p className="font-body text-2xl text-foreground/70 mb-8 max-w-2xl mx-auto">
                En detaljert oversikt over hvordan du vedlikeholder din malte badstue gjennom årene for optimal beskyttelse og langsiktig holdbarhet.
              </p>
              <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} href="/vedlikehold-maling"
                className="inline-block px-8 py-3 bg-[#2d5a3d] text-white font-body tracking-widest text-base font-semibold rounded-[4px] hover:bg-[#2d5a3d]/90 transition-all">
                VEDLIKEHOLDSPLAN
              </motion.a>
            </div>
          </motion.section>

          {/* CTA */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6 }} className="bg-gradient-to-br from-[#2a1810] to-[#3d2818] text-white rounded-[6px] p-12 md:p-20 text-center">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
              <p className="text-base tracking-widest-2xl font-body uppercase text-white/60 mb-6">Neste Steg</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light leading-[1.2] mb-6">
                Klar for<br /><span className="italic">Din Fargede Badstue?</span>
              </h2>
              <p className="font-body text-2xl text-white/70 mb-12 max-w-2xl mx-auto">
                Har du bestemt deg for maling? Vi hjelper deg med å finne den perfekte fargepaletten for dine omgivelser og sørger for holdbarhet i mange år fremover.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} href="/be-om-tilbud"
                  className="px-8 py-4 bg-white text-[#2a1810] font-body tracking-widest text-base font-semibold rounded-[4px] hover:bg-white/90 transition-all inline-block">
                  BE OM TILBUD
                </motion.a>
              </div>
            </motion.div>
            </motion.section>
            </div>
            </div>

      <Footer />
    </div>
  );
}